/**
 * ConnectedKeypad.js
 * Talkroute
 *
 * Created by Vel Genov on 12/30/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useMemo } from "react";
import { connect } from "react-redux";
import { Grid, Divider, Button } from "semantic-ui-react";
import { CSSTransition } from "react-transition-group";

import uiString from "@constants/uiString";
import keyConstants from "@constants/keyConstants";
import { CallTypeEnum } from "@constants/callConstants";
import { domIds } from "@constants/testConstants";

import {
  hangUp,
  setIsKeypadVisible,
  setIsMuteMic,
  setIsCallHold,
  setDtmfString,
} from "@actions/callActions";

import { getHHMMSSTime } from "@helpers/dateFormatter";

import { formatPhoneNumber } from "@services/dataClasses/CallData";

import KeyComponent, { Types } from "./KeyComponent";
import CloseIcon from "@resources/icons/cross.svg"
import FilePathHelper from "@helpers/filePathHelper";
import { GET_TEN_THOUSAND_CONTACTS, CONTACTS_INITIAL_PAGE } from "@components/Contacts/constants";
import { findFirstContactByNumberFromList } from "@helpers/functions/contacts";
import { useGetAllContactsQuery } from "@services/kingcobraApi";

 /**
   * Display the Calling To phone number field
   */
  const DisplayCallingFromNumber = ({callNumber, incomingCallNumber, callType}) => {
    let phoneNumber;
    const {data: _contacts, } = useGetAllContactsQuery({ params: { 
      pageSize: GET_TEN_THOUSAND_CONTACTS,
      page: CONTACTS_INITIAL_PAGE
    } });
    
    const contacts = useMemo(() => _contacts || [], [_contacts]);
    
    switch (callType) {
      case CallTypeEnum.OUTGOING:
        phoneNumber = callNumber;
        break;
      case CallTypeEnum.INCOMING:
        phoneNumber = incomingCallNumber;
        break;
      default:
        phoneNumber = callNumber;
    }

    const contact = useMemo(() => phoneNumber ? findFirstContactByNumberFromList(phoneNumber, contacts) : null, [phoneNumber, contacts]);

    if (phoneNumber) {
      const formattedPhoneNumber = contact ? contact.name : formatPhoneNumber(phoneNumber);

      return (
        <CSSTransition
          key="callingTo"
          in
          appear
          timeout={300}
          classNames="fade"
        >
          <div
            className="text-normal none-select-text"
            style={{
              textAlign: "center",
              paddingTop: "10px",
              fontSize: "25px",
              lineHeight: "25px",
              height: "36px",
            }}
          >
            {`${formattedPhoneNumber}`}
          </div>
        </CSSTransition>
      );
    }

    return <>{uiString.calls.UNKNOWN}</>;
  };
interface IConnectedKeypadProps {
  callNumber: string;
  fromPhoneNumber: string;
  incomingCallNumber: string;
  dialedNumber: string;
  callType: string;
  connectedTime: number;
  isKeypadVisible: boolean;
  isMuteMic: boolean;
  isCallHold: boolean;
  dtmfString: string;
  outgoingPhoneNumber: string,
  hangUp: () => any;
  setIsKeypadVisible: (value: boolean) => any;
  setIsMuteMic: (value: boolean) => any;
  setIsCallHold: (value: boolean) => any;
  setDtmfString: (value: string) => any;
}

/**
 * ConnectedKeypad component
 */
export class ConnectedKeypad extends React.Component<
  IConnectedKeypadProps,
  any
> {
  public inputField;
  public timer;
  public audioPlayer = new Audio();
  static defaultProps = {
    callNumber: "",
    fromPhoneNumber: "",
    isKeypadVisible: false,
    isMuteMic: false,
    isCallHold: false,
    dtmfString: "",
    incomingCallNumber: "",
    dialedNumber: "",
    callType: CallTypeEnum.OUTGOING,
  };

  /**
   * Display the Calling To phone number field
   */
  showCallingToPhoneNumber = () => {
    const { callNumber, incomingCallNumber, callType } = this.props;

    return <DisplayCallingFromNumber 
      callNumber={callNumber} 
      incomingCallNumber={incomingCallNumber} 
      callType={callType}/>;
  };

  /**
   * Display the Calling From phone number field
   */
  showCallingFromPhoneNumber = () => {
    const { outgoingPhoneNumber, dialedNumber, callType } = this.props;
    let phoneNumber;
    let label;

    switch (callType) {
      case CallTypeEnum.OUTGOING:
        phoneNumber = outgoingPhoneNumber;
        label = uiString.calls.FROM;
        break;
      case CallTypeEnum.INCOMING:
        phoneNumber = dialedNumber;
        label = uiString.calls.TO;
        break;
      default:
        phoneNumber = outgoingPhoneNumber;
        label = uiString.calls.FROM;
    }


    if (phoneNumber) {
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber);

      return (
        <CSSTransition
          key="callingFrom"
          in
          appear
          timeout={300}
          classNames="fade"
        >
          <div
            className="text-small none-select-text"
            style={{
              textAlign: "center",
              fontSize: "18px",
              padding: "5px 0 0 0",
              height: "30px",
            }}
          >
            {`${label} ${formattedPhoneNumber}`}
          </div>
        </CSSTransition>
      );
    }

    return uiString.calls.UNKNOWN;
  };

  /**
   * Processes Key Down event
   */
  onKeyDown = (event) => {
    const { keyCode, shiftKey } = event;
    let keyDownNumber = -1;

    if (shiftKey) {
      if (keyCode === keyConstants.CODE_3) {
        keyDownNumber = 10;
      }

      if (keyCode === keyConstants.CODE_8) {
        keyDownNumber = 11;
      }
    } else {
      if (keyCode >= keyConstants.CODE_0 && keyCode <= keyConstants.CODE_9) {
        keyDownNumber = keyCode - keyConstants.CODE_0;
      }

      if (
        keyCode >= keyConstants.CODE_NUM_0 &&
        keyCode <= keyConstants.CODE_NUM_9
      ) {
        keyDownNumber = keyCode - keyConstants.CODE_NUM_0;
      }
    }

    if (keyDownNumber >= 0) {
      this.onNumPadClick(keyDownNumber);
    }

    this.setState({ keyDownNumber });
  };

  /**
   * Processes Key Up event
   */
  onKeyUp = () => {
    this.setState({ keyDownNumber: -1 });
  };

  /**
   * Display the DTMF text field, allowing users to send DTMF commands
   */
  showDTMFField = () => {
    const { dtmfString } = this.props;

    return (
      <CSSTransition key="dtmf" in appear timeout={300} classNames="fade">
        <div>
          <div>
            <div
              style={{
                display: "inline-flex",
                width: "300px",
              }}
            >
              <textarea
                id={domIds.DTMF_STRING_INPUT}
                rows={1}
                value={dtmfString}
                className="text-large"
                style={{
                  fontSize: "35px",
                  textAlign: "center",
                  border: "none",
                  width: "300px",
                  outline: "none",
                  backgroundColor: "var(--color-background-grey)",
                  resize: "none",
                  whiteSpace: "nowrap",
                  overflowY: "hidden"
                }}
                onKeyDown={(e) => this.onKeyDown(e)}
                onKeyUp={() => this.onKeyUp()}
                onChange={() => {}}
                ref={(input) => {
                  this.inputField = input;
                }}
              />
              {this.displayRemoveButton()}
            </div>
          </div>
          <div style={{ height: "24px" }}>
            <Divider
              style={{
                width: "440px",
                display: "inline-flex",
                borderTop: "1px solid var(--color-dark-grey)",
                paddingBottom: "1em",
              }}
            />
          </div>
        </div>
      </CSSTransition>
    );
  };

  /**
   * Removes the text of phone number input field
   */
  handleRemoveButton = () => {
    const { setDtmfString: setDtmfStringLocal } = this.props;

    setDtmfStringLocal("");
    this.inputField.focus();
  };

  /**
   * Displays Remove Icon in the input field
   */
  displayRemoveButton = () => {
    const { dtmfString } = this.props;

    if (dtmfString.length === 0) return null;

    return (
      <Button
        basic
        icon
        onClick={() => this.handleRemoveButton()}
        style={{ boxShadow: "none", padding: 0, paddingLeft: 5 }}
        className="simple-button"
      >
       <CloseIcon
        style={{
          position: "absolute",
          top: "30px",
          margin: "0px",
          padding: "0px",
          fill: "var(--color-pumpkin-orange)",
          cursor: "pointer"
        }}
      />
      </Button>
    );
  };

  /**
   * Process a click on the end call button
   */
  onEndCallButtonClick = () => {
    const { hangUp: hangUpLocal } = this.props;
    hangUpLocal();
  };

  /**
   * Process a click on the end num pad
   */
  onNumPadClick = (clickedButton: number | string) => {
    const { dtmfString, setDtmfString: setDtmfStringLocal } = this.props;
    let clickedStr = clickedButton;

    if (clickedButton === 10) {
      clickedStr = "#";
    }

    if (clickedButton === 11) {
      clickedStr = "*";
    }

    const updatedDTMF = `${dtmfString}${clickedStr}`;

    setDtmfStringLocal(updatedDTMF);
  };

  /**
   * Process a click on the toggle keypad button
   */
  onToggleKeypad = () => {
    const {
      isKeypadVisible,
      setIsKeypadVisible: setIsKeypadVisibleLocal,
    } = this.props;

    setIsKeypadVisibleLocal(!isKeypadVisible);
  };

  /**
   * Process a click on the hold button
   */
  onHoldButtonClick = () => {
    const { isCallHold, setIsCallHold: setIsCallHoldLocal } = this.props;

    setIsCallHoldLocal(!isCallHold);
  };

  /**
   * Process a click on the mute microphone button
   */
  onMuteMic = () => {
    const { isMuteMic, setIsMuteMic: setIsMuteMicLocal } = this.props;

    setIsMuteMicLocal(!isMuteMic);
  };

  /**
   * Set the timestamp to current timestamp
   */
  setTimestamp = () => {
    const curTimestamp = Date.now();

    this.setState({ timestamp: curTimestamp });
  };

  constructor(props) {
    super(props);

    this.state = {
      timestamp: Date.now(),
      keyDownNumber: -1,
    };

    this.timer = null;
  }

  componentDidMount() {
    this.timer = setInterval(this.setTimestamp, 500);
    const filePath = new FilePathHelper().getFullFilePath(
      `sounds/DTMF_Beep.m4a`
    );
    this.audioPlayer.src = filePath;
  }

  componentDidUpdate(prevProps) {
    const {
      isKeypadVisible: prevIsKeypadVisible,
      dtmfString: prevDtmfString,
    } = prevProps;
    const { isKeypadVisible, dtmfString } = this.props;

    if (prevIsKeypadVisible !== isKeypadVisible && isKeypadVisible) {
      this.inputField.focus();
    }

    if (prevDtmfString !== dtmfString) {
      this.inputField.scrollTop = this.inputField.scrollHeight;
    }
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  render() {
    const { timestamp, keyDownNumber } = this.state;
    const {
      connectedTime,
      isKeypadVisible,
      isMuteMic,
      isCallHold,
    } = this.props;
    const buttonType = Types.text;
    const diffTimestamp =
      timestamp - connectedTime < 0 ? 0 : timestamp - connectedTime;
    const statusString = getHHMMSSTime(diffTimestamp);
    const muteType = isMuteMic ? Types.unmute : Types.mute;
    const holdType = isCallHold ? Types.unhold : Types.hold;

    return (
      <Grid
        textAlign="center"
        verticalAlign="middle"
        style={{
          height: "100%",
          width: "100%",
          margin: "0px",
          backgroundColor: "var(--color-background-grey)",
        }}
      >
        <Grid.Column style={{ padding: "0px" }}>
          {/* Timer Field */}
          <div
            className="text-normal none-select-text"
            style={{
              textAlign: "center",
              fontSize: "18px",
              height: "20px",
            }}
          >
            {statusString}
          </div>

          {/* DTMF Field  */}
          {isKeypadVisible === true ? this.showDTMFField() : null}
          {/* To Field */}
          {isKeypadVisible === true ? null : this.showCallingToPhoneNumber()}
          {/* From Field */}
          {isKeypadVisible === true ? null : this.showCallingFromPhoneNumber()}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <div className="keypad_container">
              {/* Row 1 */}
              <KeyComponent
                id={domIds.CONNECTED_KEYPAD_1_NUM_BUTTON}
                keyTitle="1"
                clickActionCallback={() => this.onNumPadClick(1)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 1}
              />
              <KeyComponent
                keyTitle="2"
                keySubtitle="ABC"
                clickActionCallback={() => this.onNumPadClick(2)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 2}
              />
              <KeyComponent
                keyTitle="3"
                keySubtitle="DEF"
                clickActionCallback={() => this.onNumPadClick(3)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 3}
              />

              {/* Row 2 */}

              <KeyComponent
                keyTitle="4"
                keySubtitle="GHI"
                clickActionCallback={() => this.onNumPadClick(4)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 4}
              />
              <KeyComponent
                keyTitle="5"
                keySubtitle="JKL"
                clickActionCallback={() => this.onNumPadClick(5)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 5}
              />
              <KeyComponent
                keyTitle="6"
                keySubtitle="MNO"
                clickActionCallback={() => this.onNumPadClick(6)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 6}
              />

              {/* Row 3 */}

              <KeyComponent
                keyTitle="7"
                keySubtitle="PQRS"
                clickActionCallback={() => this.onNumPadClick(7)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 7}
              />
              <KeyComponent
                keyTitle="8"
                keySubtitle="TUV"
                clickActionCallback={() => this.onNumPadClick(8)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 8}
              />
              <KeyComponent
                keyTitle="9"
                keySubtitle="WXYZ"
                clickActionCallback={() => this.onNumPadClick(9)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 9}
              />

              {/* Row 4 */}

              <KeyComponent
                keyTitle="*"
                clickActionCallback={() => this.onNumPadClick("*")}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 11}
              />
              <KeyComponent
                keyTitle="0"
                keySubtitle="+"
                clickActionCallback={() => this.onNumPadClick(0)}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 0}
              />
              <KeyComponent
                keyTitle="#"
                clickActionCallback={() => this.onNumPadClick("#")}
                type={buttonType}
                isSoundEnabled
                audioPlayer={this.audioPlayer}
                isVisible={isKeypadVisible}
                isPressed={keyDownNumber === 10}
              />
            </div>
            <div style={{ display: "flex", width: "333px" }}>
              {/* Row 5 */}

              <KeyComponent
                id={domIds.CONNECTED_KEYPAD_TOGGLE_KEYPAD_BUTTON}
                type={isKeypadVisible ? Types.keypadActive : Types.keypad}
                clickActionCallback={() => this.onToggleKeypad()}
              />
              <KeyComponent
                id={domIds.CONNECTED_KEYPAD_END_CALL_BUTTON}
                type={Types.end}
                clickActionCallback={() => this.onEndCallButtonClick()}
              />
              <KeyComponent
                id={domIds.CONNECTED_KEYPAD_MUTE_BUTTION}
                type={muteType}
                clickActionCallback={() => this.onMuteMic()}
              />
              <KeyComponent
                id={domIds.CONNECTED_KEYPAD_HOLD_BUTTION}
                type={holdType}
                clickActionCallback={() => this.onHoldButtonClick()}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid>
    );
  }
}

// Redux data binding
const mapStateToProps = (state) => ({
  callNumber: state.callReducer.callNumber,
  incomingCallNumber: state.callReducer.incomingCallNumber,
  outgoingPhoneNumber: state.callReducer.outgoingPhoneNumber,
  dialedNumber: state.callReducer.dialedNumber,
  callType: state.callReducer.callType,
  connectedTime: state.callReducer.connectedTime,
  isKeypadVisible: state.callReducer.isKeypadVisible,
  isMuteMic: state.callReducer.isMuteMic,
  isCallHold: state.callReducer.isCallHold,
  dtmfString: state.callReducer.dtmfString,
});

export default connect(mapStateToProps, {
  hangUp,
  setIsKeypadVisible,
  setIsMuteMic,
  setIsCallHold,
  setDtmfString,
})(ConnectedKeypad);
